import { useMemo, useRef } from 'react';
import useStaticProductListingPageData from '../useStaticProductListingPageData';
const usePaginatedStaticProductListingPageData = ({
  productIds: allProductIds,
  showSearchProductsOnly = true,
  algoliaHits,
  algoliaFilters,
  facetFilters,
  isAlgoliaInitialDataLoading,
  resetAlgoliaFilters,
  setActiveAlgoliaFilter,
  serverSidePaginationEnabled = false,
  fetchLimit
}, {
  skipSorting = true
}) => {
  const productIds = useMemo(() => allProductIds?.length ? allProductIds.slice(0, fetchLimit) : [], [allProductIds, fetchLimit]);
  const fetchedIdsRef = useRef([]);
  const accumulatedProductsRef = useRef([]);

  // detect new product IDs not yet fetched
  const newProductIds = useMemo(() => {
    // When productIds <= 12 → treat as reset
    if (productIds.length <= 12) {
      fetchedIdsRef.current = [];
      accumulatedProductsRef.current = [];
      return productIds;
    }

    // Otherwise, only take the ones we haven’t seen before
    return productIds.filter(id => !fetchedIdsRef.current.includes(id)) ?? [];
  }, [productIds]);

  // fetch initial batch or updates
  const currentData = useStaticProductListingPageData({
    productIds: newProductIds.length ? newProductIds : productIds,
    showSearchProductsOnly,
    algoliaHits,
    algoliaFilters,
    facetFilters,
    isAlgoliaInitialDataLoading,
    resetAlgoliaFilters,
    setActiveAlgoliaFilter,
    serverSidePaginationEnabled
  }, {
    skipSorting,
    skip: productIds.length === 0
  });

  // accumulate new data when new IDs were fetched successfully
  const accumulatedData = useMemo(() => {
    const {
      products = []
    } = currentData;

    // Only append if there are new IDs and non-empty products
    if (newProductIds.length && products.length) {
      fetchedIdsRef.current = [...fetchedIdsRef.current, ...newProductIds];
      accumulatedProductsRef.current = [...accumulatedProductsRef.current, ...products];
    } else if (!accumulatedProductsRef.current.length && products.length && productIds.length <= 12) {
      // first load
      fetchedIdsRef.current = [...productIds];
      accumulatedProductsRef.current = [...products];
    }
    return {
      ...currentData,
      products: accumulatedProductsRef.current,
      loading: false,
      isAlexandriaInitialDataLoading: fetchLimit === 12 && currentData.isAlexandriaDataLoading
    };
  }, [currentData, newProductIds, productIds, fetchLimit]);
  return accumulatedData;
};
export default usePaginatedStaticProductListingPageData;